import {
  BaseWebComponentController,
  IListenerConfig,
  WebComponentEvent,
} from '../../../../framework/BaseWebComponentController';
import { CompanyBetaProgramsTabWebComponent } from '..';
import { templateCompanyBetaProgramsTab } from '../view/template';
import { CompanyBetaProgramsTabRepository } from '../repository/CompanyBetaProgramsTabRepository';
import { CompanyBetaProgramsTabView } from '../view/CompanyBetaProgramsTabView';
import { BetaProgramsEvents, GetBetaProgramsResponse } from '../types/types';
import { RadioEvent } from '../../../../ui/gzRadio/types';
import { GzButtonWebComponent } from '../../../../ui/gzButton';
import { GzToast, ToastSeverity } from '../../../../ui/gzToastNotification/type';
import { GzToastNotificationWebComponent } from '../../../../ui/gzToastNotification';
import { toggleVisibility } from '../../../../utils/htmlElement.utils';
import { GzRadioWebComponent } from '../../../../ui/gzRadio';
import { GzIconButtonWebComponent } from '../../../../ui/gzIconButton';
import { ButtonColor } from '../../../../ui/gzIconButton/types';
import { FeedbackHelper } from '../../../../utils/feedback/feedback.helper';
import { DashboardPopUpType } from '../../../../genericDashboard/utils/dashboard.pop-up.factory';

export class CompanyBetaProgramsTabController extends BaseWebComponentController {
  public component: CompanyBetaProgramsTabWebComponent;
  public dataRepository: CompanyBetaProgramsTabRepository;
  public dataRepositoryConstructor: typeof CompanyBetaProgramsTabRepository;
  public view: CompanyBetaProgramsTabView;
  public viewConstructor: typeof CompanyBetaProgramsTabView;

  constructor(component: CompanyBetaProgramsTabWebComponent) {
    super(component);

    this.initialSetup({
      viewConstructor: CompanyBetaProgramsTabView,
      dataRepositoryConstructor: CompanyBetaProgramsTabRepository,
      viewTemplate: templateCompanyBetaProgramsTab,
    });
  }

  public async getDataForView(): Promise<GetBetaProgramsResponse> {
    return await this.dataRepository.getAllAvailableBetaPrograms();
  }

  computeEventListeners(): IListenerConfig[] {
    return [
      {
        element: this.view.dom.$betaProgramsSection,
        event: RadioEvent.SELECTION_CHANGED,
        callback: this.switchButtonsState,
      },
      {
        element: this.view.dom.$buttonsContainer,
        event: BetaProgramsEvents.ATTEMPT_ENROLLMENT,
        callback: this.createDialog,
      },
    ];
  }

  private switchButtonsState(ev: WebComponentEvent) {
    this.view.switchButtonsState(ev.detail.selectedValue);
  }

  private createDialog(ev: WebComponentEvent) {
    const { eventName, selectedProgram } = ev.detail;
    const dialogConfirmCallback = async () => {
      const confirmBtn = dialog.shadowRoot?.querySelector<GzButtonWebComponent>('#confirm');

      if (confirmBtn) {
        confirmBtn.showSpinner = true;
      }

      if (eventName === BetaProgramsEvents.OPTOUT) {
        const betaProgram = this.view.getBetaProgramByType(selectedProgram);

        if (betaProgram.hasFeedback) {
          FeedbackHelper.createFeedbackElement(betaProgram.type, DashboardPopUpType.FEEDBACK_VIEW_DIALOG, false, null);
        }
        await this.updateBetaProgramEnrollment(false, selectedProgram);
      } else {
        await this.updateBetaProgramEnrollment(true, selectedProgram);
      }

      dialog.close();
    };

    const dialog = this.view.createDialog(eventName, dialogConfirmCallback);
  }

  private createToastNotification(actionSuccessful: boolean, message: string) {
    const severity = actionSuccessful ? ToastSeverity.SUCCESS : ToastSeverity.ERROR;
    const toastNotificationConfigs: GzToast = {
      message,
      hasAction: false,
      severity,
      keepOpen: !actionSuccessful,
      autoCloseDelayMs: 10000,
    };

    GzToastNotificationWebComponent.buildToastWrapperElement(toastNotificationConfigs);
  }

  private async updateBetaProgramEnrollment(state: boolean, selectedProgram: string) {
    const response = await this.dataRepository.updateEnrollmentState(parseInt(selectedProgram, 10), state);

    let toastMessage;
    if (response.success) {
      toastMessage = state
        ? this.dependencies.translateFn('Enrollment successful.')
        : this.dependencies.translateFn('Opt-out successful.');
    } else {
      toastMessage = state
        ? this.dependencies.translateFn('Enrollment failed.')
        : this.dependencies.translateFn('Opt-out failed.');
    }

    this.createToastNotification(response.success, toastMessage);

    const warningMessage = state
      ? this.dependencies.translateFn(
          'You have successfully enrolled your company in an Early Access program. All GravityZone users must log out and log back in for changes to take effect.'
        )
      : this.dependencies.translateFn(
          'You have successfully opted your company out of an Early Access program. All GravityZone users must log out and log back in for changes to take effect.'
        );

    if (response.success) {
      this.render();
      this.view.dom.$warningMessage.innerHTML = warningMessage;
      toggleVisibility(this.view.dom.$warningMessage, true);
    } else {
      if (this.view.dom.$betaProgramsSection.shadowRoot) {
        const rows = this.view.dom.$betaProgramsSection.shadowRoot.querySelectorAll('.beta-program-row');
        rows.forEach(item => {
          const radio = item.querySelector('gz-radio') as GzRadioWebComponent;
          if (radio.value === selectedProgram) {
            const iconBtn = item.querySelector('gz-icon-button') as GzIconButtonWebComponent;
            if (iconBtn) {
              iconBtn.icon = 'ds-icon-notification_negative';
              iconBtn.classList.remove('hidden');
              iconBtn.buttonColor = ButtonColor.NEGATIVE;
            }
          }
        });
      }
    }
  }
}
